/* ============================================================
   YOLANDA — nano-flight
   One unbroken shot, full bleed, scrubbed by scroll.

   The whole section is a single continuous descent: mountain
   ridge -> canopy -> forest floor -> into rock -> groundwater ->
   pine sapwood -> the nanobubble field -> the pouch. It is ONE
   video file, so there are no seams to hide and no legs to
   crossfade.

   Three things here are not obvious and all three are load-bearing:

   1. CLIP TIME IS NOT SCROLL TIME. The stage is on screen for one
      viewport before its pinned travel begins and one after it
      ends. Driving the playhead from pinned progress parks the
      film on frame one through the whole entry and on its last
      frame through the whole exit, so the reader watches a still
      photograph slide up the page. The clip is mapped across the
      stage's ENTIRE visible life instead.

   2. THE PLAYHEAD IS LERPED. Scroll never writes currentTime
      directly; it writes a target and a rAF loop walks toward it.
      Wheel events do not arrive at a constant rate, and a 1:1
      write reproduces every gap in them as a stutter.

   3. iOS WILL NOT PAINT AN UNPLAYED MUTED VIDEO. Seeks land,
      `seeked` fires, and the picture stays on one frame. The
      decoder has to be primed with one play()/pause(), and the
      prime must be re-attemptable — a one-shot prime is spent
      while the file is still downloading, which is exactly how a
      hero ends up frozen on a real phone while every desktop
      probe reports green.
   ============================================================ */
(function () {
  "use strict";

  var root = document.querySelector("[data-flight]");
  if (!root) return;

  var stage = root.querySelector("[data-flight-stage]");
  var video = root.querySelector("[data-flight-video]");
  var poster = root.querySelector("[data-flight-poster]");
  if (!stage) return;

  /* The video is OPTIONAL. Until the flight is uploaded to Files and picked
     in the theme editor, the section renders with a poster and no <video> at
     all — and the waypoint copy still has to arrive, or the hero is a
     photograph with nothing written on it. Only the playhead depends on the
     film; the cues and the nav never do. */
  var hasVideo = !!video;

  var mReduce = window.matchMedia("(prefers-reduced-motion: reduce)");
  var conn = navigator.connection || {};
  var saveData = conn.saveData === true;
  var coarse = window.matchMedia("(pointer: coarse)").matches;

  /* Reduced motion and Save-Data never fetch the file at all: the poster
     holds and the waypoint copy still cues, so the section keeps its
     meaning without paying for megabytes nobody asked for. */
  var stills = mReduce.matches || saveData;

  var srcDesktop = hasVideo ? video.getAttribute("data-src") : null;
  var srcMobile = hasVideo ? video.getAttribute("data-src-mobile") : null;
  var src = (coarse && srcMobile) ? srcMobile : srcDesktop;

  /* ---------------------------------------------- state */
  var vh = window.innerHeight;
  var loaded = false, loading = false, painted = false;
  var target = 0, current = 0, duration = 0;
  var seeking = false, seekIssued = 0, lastWrite = -1;
  var primed = false, priming = false;

  /* A write smaller than this costs a seek and shows nothing. On a phone it
     costs a great deal more than it shows: 0.020s of a 35s clip spread over
     nine screens is about four pixels of scroll, so almost every scroll event
     pushed a fresh seek into a 720p decoder. 0.06s is roughly twelve pixels
     and under three frames of the clip -- well below what reads as a step,
     and a fraction of the decode work. */
  var DEADBAND = coarse ? 0.060 : 0.008;
  var LERP = coarse ? 0.24 : 0.18;

  /* ---------------------------------------------- loading */

  function markReady() {
    if (painted) return;
    painted = true;
    root.setAttribute("data-flight-state", "playing");
  }

  /* Blob first: fetching the file and seeking a blob URL is the most
     reliable way to scrub, because it needs no range-request support from
     the CDN. But Shopify serves video cross-origin, so if CORS blocks the
     fetch we fall back to a direct src rather than shipping a dead hero. */
  function load() {
    if (!hasVideo || loaded || loading || stills || !src) return;
    loading = true;
    root.setAttribute("data-flight-state", "loading");

    fetch(src, { mode: "cors", credentials: "omit" })
      .then(function (r) {
        if (!r.ok) throw new Error("http " + r.status);
        return r.blob();
      })
      .then(function (b) { attach(URL.createObjectURL(b), "blob"); })
      .catch(function () { attach(src, "direct"); });
  }

  function attach(url, how) {
    loaded = true;
    root.setAttribute("data-flight-load", how);

    /* The veil can only measure the film on the blob path, and that is on
       purpose. A blob URL is same-origin, so its canvas is never tainted --
       no `crossorigin` attribute needed, and none wanted: adding one makes
       the element refuse to load at all from any source that ever stops
       sending CORS headers, which is a far worse failure than falling back
       to a fixed density here. */
    if (how === "blob") {
      var c = document.createElement("canvas");
      c.width = 16; c.height = 16;
      var ctx = c.getContext("2d", { willReadFrequently: true });
      if (ctx) { sampler = { c: c, ctx: ctx }; sampleable = true; }
    }
    if (!sampleable) setVeil(0.6);

    video.addEventListener("loadedmetadata", function () {
      duration = video.duration || 0;
      prime();
      write(target, true);
    }, { once: true });
    video.addEventListener("seeked", function () {
      seeking = false;
      markReady();
    });
    /* A clip already sitting at time 0 never fires `seeked`, and iOS can
       leave `seeking` true forever. Never gate the reveal on the event alone. */
    setTimeout(markReady, 2500);
    video.src = url;
    video.load();
  }

  /* ---------------------------------------------- the iOS prime */

  function prime() {
    if (!hasVideo || primed || priming || !video.src) return;
    priming = true;
    var p = video.play();
    /* iOS can leave this promise pending forever, which would strand the
       flag and block every later attempt. Release it on a timer too. */
    setTimeout(function () { priming = false; }, 1200);
    if (p && p.then) {
      p.then(function () {
        video.pause();
        primed = true; priming = false;
        markReady();
      }).catch(function () { priming = false; });
    } else {
      try { video.pause(); } catch (e) {}
      primed = true; priming = false;
    }
  }

  /* touchend matters: the HTML spec's activation-triggering events include
     it but NOT touchstart, so a phone in Low Power Mode that rejects the
     touchstart attempt still gets a valid one when the finger lifts. */
  ["touchstart", "touchend", "pointerdown", "click", "scroll"].forEach(function (ev) {
    window.addEventListener(ev, function () { if (!primed) prime(); }, { passive: true });
  });

  /* ---------------------------------------------- the playhead */

  function write(t, force) {
    if (!hasVideo || !loaded || !duration) return;
    if (!force && Math.abs(t - lastWrite) < DEADBAND) return;
    /* Never queue a seek while the decoder is still resolving the last one:
       a fast flick otherwise piles them up and the picture freezes. */
    if (seeking && !force) {
      if (performance.now() - seekIssued < 700) return;
      /* stuck past 700ms — iOS does this. Re-issue rather than wait forever. */
    }
    lastWrite = t;
    seeking = true;
    seekIssued = performance.now();
    try { video.currentTime = t; } catch (e) { seeking = false; }
  }

  /* ---------------------------------------------- the veil

     A fixed scrim cannot serve this film. Measured across the whole 35.6s clip
     at 2fps, taking the 90th percentile of the region the copy occupies: the
     footage is dark for about 85% of its length (median p90 44/255 on the
     desktop cut, 51 on the portrait one) and then spikes to 227 and 236 in two
     places -- the opening misted ridge, which is the landing view every
     visitor sees, and the closing pouch waypoint carrying the CTA. A scrim
     heavy enough for 236 would flatten everything else.

     So the scrim reads the frame. Every few frames the region behind the
     active waypoint is drawn into a 16x16 canvas, the 90th percentile of its
     luminance taken, and the density needed to bring that under the 4.5:1
     threshold solved for and published as --nf-veil.

     The 90th percentile, not the mean. During the original build a headline
     measured 5.30:1 against the mean of its background and 1.85:1 against the
     brightest patch of it, and the brightest patch is where the eye actually
     loses the letters. */

  var SAMPLE_EVERY = coarse ? 10 : 7;   /* ~6Hz / ~8.5Hz at 60fps */
  /* 104 is where #DCE8F4 clears 4.5:1 against a flat background. Aim under it,
     because the two measurements are not the same measurement: this samples
     the p90 of a whole copy-block-sized region of the FILM, while the contrast
     floor is the p99 of the composited pixels under ONE line. The gap between
     those is the headroom, and 96 was not quite enough of it. */
  var VEIL_TARGET = 90;
  var VEIL_SCRIM = 16;                  /* luma of the scrim's own rgb(2 18 34) */
  var VEIL_MAX = 0.82;
  var veil = 0.68, veilWritten = -1, frameNo = 0;
  var sampler = null, sampleable = false;
  var cueRect = null, cueRectFor = null, geomEpoch = 0, cueRectEpoch = -1;

  function setVeil(v) {
    /* Only touch the property when it actually moves. Every write invalidates
       two full-stage gradients. */
    if (Math.abs(v - veilWritten) < 0.01) return;
    veilWritten = v;
    root.style.setProperty("--nf-veil", v.toFixed(3));
  }

  function sampleVeil() {
    if (!sampleable || !video.videoWidth || !video.videoHeight) return;
    /* Never read back immediately after issuing a seek: drawImage from a video
       mid-seek forces a GPU->CPU sync and stalls the frame. */
    if (performance.now() - seekIssued < 60) return;

    var sr = stage.getBoundingClientRect();
    if (!sr.width || !sr.height) return;

    /* The region to sample is where the active waypoint sits. It is absolutely
       positioned inside the sticky stage, so relative to the stage it does not
       move while scrolling -- measure it only when the active cue changes or
       the viewport does, rather than once per sample. */
    var el = null, best = 0;
    for (var i = 0; i < cues.length; i++) {
      if (cues[i].o > best) { best = cues[i].o; el = cues[i].el; }
    }
    if (el && best > 0.05) {
      if (el !== cueRectFor || cueRectEpoch !== geomEpoch) {
        var er = el.getBoundingClientRect();
        cueRect = { x: er.left - sr.left, y: er.top - sr.top, w: er.width, h: er.height };
        cueRectFor = el; cueRectEpoch = geomEpoch;
      }
    } else if (!cueRect || cueRectEpoch !== geomEpoch) {
      /* between waypoints: the band where copy is about to arrive */
      cueRect = { x: 0, y: sr.height * 0.58, w: sr.width * 0.62, h: sr.height * 0.42 };
      cueRectFor = null; cueRectEpoch = geomEpoch;
    }

    /* Map that screen rect back through `object-fit: cover` into the video's
       own pixels. Skipping this samples the wrong part of the frame on every
       viewport whose aspect differs from the clip's -- which is all of them on
       a phone, where a 9:16 clip fills a 390x844 stage. */
    var vW = video.videoWidth, vH = video.videoHeight;
    var scale = Math.max(sr.width / vW, sr.height / vH);
    var dx = (sr.width - vW * scale) / 2;
    var dy = (sr.height - vH * scale) / 2;

    var sx = (cueRect.x - dx) / scale;
    var sy = (cueRect.y - dy) / scale;
    var sw = cueRect.w / scale;
    var sh = cueRect.h / scale;
    sx = Math.max(0, Math.min(sx, vW - 2));
    sy = Math.max(0, Math.min(sy, vH - 2));
    sw = Math.max(2, Math.min(sw, vW - sx));
    sh = Math.max(2, Math.min(sh, vH - sy));

    var d;
    try {
      sampler.ctx.drawImage(video, sx, sy, sw, sh, 0, 0, 16, 16);
      d = sampler.ctx.getImageData(0, 0, 16, 16).data;
    } catch (e) {
      /* A tainted canvas throws here rather than at draw time. Stop sampling
         and hold a density that covers the film's worst frame, because from
         this point on we are blind to it. */
      sampleable = false;
      setVeil(0.6);
      return;
    }

    var lums = [];
    for (var p = 0; p < d.length; p += 4) {
      lums.push(0.2126 * d[p] + 0.7152 * d[p + 1] + 0.0722 * d[p + 2]);
    }
    lums.sort(function (a, b) { return a - b; });
    var p90 = lums[Math.floor(lums.length * 0.9)];

    var need = p90 <= VEIL_TARGET ? 0 : (p90 - VEIL_TARGET) / (p90 - VEIL_SCRIM);
    var k = Math.max(0, Math.min(need, VEIL_MAX)) / VEIL_MAX;

    /* Asymmetric, and the asymmetry is the point. Needing MORE density is a
       legibility failure and has to be corrected before the reader has time to
       squint, so the rise is nearly immediate -- about a third of a second,
       measured, for a dark-to-bright cut. Needing LESS is only aesthetic, so
       the fall is lazy enough that the scrim never pumps shot to shot.

       The fall was slower still at first, and it took over two seconds to
       settle: long enough that the scrim visibly lagged the film after the
       reader stopped scrolling. */
    veil += (k - veil) * (k > veil ? 0.5 : 0.12);
    setVeil(veil);
  }

  /* Progress across the stage's whole visible life, not its pinned travel.
     See note 1 at the top of this file. Takes the rect rather than reading
     one: getBoundingClientRect forces a layout, and the loop below already
     needs the same numbers for its liveness test. */
  function progress(r) {
    var travel = r.height - vh;
    if (travel <= 0) return 0;
    return Math.min(Math.max(-r.top / travel, 0), 1);
  }

  var running = false;

  /* ONE loop drives both the playhead and the waypoint copy, off ONE rect
     read, and it stops the moment the stage leaves the screen.

     It used to be two independent rAF loops. The playhead's read its rect
     three times a frame -- twice for the liveness test, once inside
     progress() -- and the cue loop never stopped at all: it re-armed itself
     unconditionally, so it went on forcing a layout and writing to six
     elements every frame for the whole life of the page, including while the
     reader was several screens below looking at the spec table. On a phone
     that is a permanent tax on exactly the scrolling it decorates. */
  function loop() {
    var r = root.getBoundingClientRect();
    if (!(r.bottom > -vh * 0.5 && r.top < vh * 1.5)) { running = false; return; }

    var p;

    if (auto && duration) {
      /* PLAYBACK INVERTS THE USUAL RELATIONSHIP. Normally scroll is the clock
         and the playhead follows it. Here the film is the clock and the page
         follows, which is the only way to get smooth playback: the deadband is
         0.06s on a phone and 1x advances the clip 0.0167s per frame, so
         scrubbing at playback speed would skip three writes in four and step at
         about 15fps. Played natively the decoder never seeks at all.

         progress comes from currentTime rather than from the scroll we are
         about to set, so the cues stay frame-exact instead of a frame behind. */
      p = Math.min(video.currentTime / duration, 1);
      var heroTop = window.scrollY + r.top;      /* invariant while playing */
      var travel = r.height - vh;
      if (travel > 0) window.scrollTo(0, heroTop + travel * p);
      if (p >= 1 || video.ended) finishAuto();
    } else {
      p = progress(r);
      if (hasVideo && duration) {
        target = p * duration;
        current += (target - current) * LERP;
        if (Math.abs(target - current) < 0.004) current = target;
        write(current);
      }
    }

    paintCues(p);
    if (++frameNo % SAMPLE_EVERY === 0) sampleVeil();

    requestAnimationFrame(loop);
  }

  function tick() {
    var r = root.getBoundingClientRect();
    /* fetch earlier than we drive: a reader who scrolls briskly should not
       arrive at a stage that is still downloading */
    if (hasVideo && r.top < vh * 2.5 && r.bottom > -vh) load();
    if (!running && r.bottom > -vh * 0.5 && r.top < vh * 1.5) {
      running = true;
      requestAnimationFrame(loop);
    }
  }

  addEventListener("scroll", tick, { passive: true });
  addEventListener("resize", function () {
    vh = window.innerHeight;
    geomEpoch++;   /* the cached copy rect is now stale */
    tick();
  }, { passive: true });

  if (stills || !hasVideo) {
    root.setAttribute("data-flight-state", "stills");
    /* Nothing to measure: the poster holds, and the poster is the film's own
       first frame -- which is its BRIGHTEST, at p90 227 on the desktop cut and
       236 on the portrait one. This path gets the heavy end of the range, not
       a neutral middle. */
    setVeil(0.78);
  }
  /* Start regardless of the film: the cues ride this loop too, and gating it
     on hasVideo left a section with no video uploaded showing no copy. */
  tick();

  /* ---------------------------------------------- waypoint copy */

  /* Windows are parsed ONCE. The old loop re-read the attribute, split it and
     mapped it to floats for every cue on every frame: six string parses and
     six throwaway arrays, sixty times a second, for values that never change.

     "from to [rampIn] [rampOut]", ramps as a fraction of the window. A hero
     MUST declare rampIn 0 — the greet form. Without it the cue ramps up from
     nothing and the landing view, the one screen every visitor sees, has no
     headline on it. */
  var cues = [].slice.call(root.querySelectorAll("[data-cue]")).map(function (el) {
    var w = (el.getAttribute("data-cue") || "0 1").split(/\s+/).map(parseFloat);
    var from = w[0] || 0;
    var to = isNaN(w[1]) ? 1 : w[1];
    var span = Math.max(to - from, 0.0001);
    /* A cue that runs to the end of the flight HOLDS. Ramping it out means
       the closing waypoint -- the one carrying the CTA -- fades to nothing
       over the last fraction of a percent of scroll, so the reader arrives at
       the bottom of the hero to a bare frame with no copy and no button on
       it. The close has to resolve, not evaporate. */
    var hold = to >= 0.999;
    return {
      el: el, from: from, to: to, hold: hold,
      rIn: (isNaN(w[2]) ? 0.14 : w[2]) * span,
      rOut: hold ? 0 : (isNaN(w[3]) ? 0.14 : w[3]) * span,
      o: -1,        /* last painted opacity, so unchanged frames cost nothing */
      hidden: null  /* last painted inert state, same reason */
    };
  });

  function paintCues(p) {
    for (var i = 0; i < cues.length; i++) {
      var c = cues[i], o;
      if (p < c.from) o = 0;
      else if (c.rIn > 0 && p < c.from + c.rIn) o = (p - c.from) / c.rIn;
      else if (p < c.to - c.rOut) o = 1;
      else if (c.rOut > 0 && p < c.to) o = (c.to - p) / c.rOut;
      else o = (c.hold || p < c.to) ? 1 : 0;

      /* Only touch the DOM when the value actually moves. Writing the same
         opacity back every frame still costs a style recalculation, and at
         any given moment five of the six cues are sitting flat at zero. */
      var q = Math.round(o * 200) / 200;
      if (q !== c.o) {
        var prev = c.o;
        c.o = q;
        c.el.style.opacity = q;
        c.el.style.transform = "translate3d(0," + ((1 - q) * 14).toFixed(2) + "px,0)";
        /* Promote only while the cue is actually showing. Six permanently
           will-changed full-width layers stacked over a full-bleed video is
           GPU memory a phone has no reason to be spending. */
        if ((prev > 0) !== (q > 0)) {
          c.el.style.willChange = q > 0 ? "opacity, transform" : "auto";
        }
      }

      /* A faded waypoint still holds a real CTA. pointer-events alone leaves
         it in the tab order, so a keyboard user lands on a button nobody can
         see. `inert` removes it from the tree until the waypoint is up. */
      var hidden = q < 0.5;
      if (hidden !== c.hidden) {
        c.hidden = hidden;
        c.el.style.pointerEvents = hidden ? "none" : "auto";
        if ("inert" in c.el) c.el.inert = hidden;
        else c.el.setAttribute("aria-hidden", hidden ? "true" : "false");
      }
    }
  }

  /* ---------------------------------------------- the play control

     Plays the descent end to end and carries the page down with it, then hands
     the reader to the process section. Manual scrolling is untouched; this is
     an alternative to it, not a replacement.

     See loop() for why the film drives the scroll rather than the other way
     round. Everything else here is about giving it back cleanly. */

  var playBtn = root.querySelector("[data-flight-play]");
  var playHint = root.querySelector("[data-flight-hint]");
  var auto = false, tweening = 0, dismissed = false;

  function setAuto(state) {
    root.setAttribute("data-flight-auto", state);
    if (playBtn) playBtn.setAttribute("aria-pressed", state === "playing" ? "true" : "false");
  }

  /* Dismissal is one-way and lasts the session. The alternative -- bringing the
     control back whenever the reader returns to the top -- is a control that is
     effectively always on screen, which is the opposite of the intent.

     `inert` as well as opacity: a faded button is still in the tab order, so a
     keyboard user would otherwise land on something nobody can see. */
  /* How far the stage hangs below the fold before the sticky pin engages.

     At scroll 0 the section sits below Dawn's header, so a 100svh stage runs
     from ~66px down to ~66px past the bottom. Both controls exist only on that
     first screen, so centring them on the stage puts them measurably low and
     pushes the scroll hint off the bottom of the display entirely. */
  function measureOverhang() {
    var sr = stage.getBoundingClientRect();
    var docTop = sr.top + window.scrollY;      /* where the stage starts at rest */
    var ih = window.innerHeight;

    /* How far past the bottom of the display the stage runs. Lifts the hint. */
    root.style.setProperty("--nf-overhang",
      Math.max(0, Math.round(docTop + sr.height - ih)) + "px");

    /* How far the stage's own midpoint sits below the display's. Centring the
       disc on the stage is not centring it on the screen, and these differ by
       the whole header height -- deriving one from the other got it half right
       and left the disc visibly low. */
    root.style.setProperty("--nf-centre-shift",
      Math.round(docTop + sr.height / 2 - ih / 2) + "px");
  }

  function dismiss() {
    if (dismissed) return;
    dismissed = true;
    root.setAttribute("data-flight-cue", "off");
    if (playBtn) {
      if ("inert" in playBtn) playBtn.inert = true;
      else playBtn.setAttribute("aria-hidden", "true");
    }
  }

  /* Nothing to play: no film, or a reader who asked for reduced motion or is on
     Save-Data, for whom the clip is never fetched. Take the controls out of the
     document rather than leaving a button that cannot work. */
  if (!hasVideo || stills) {
    if (playBtn) { playBtn.remove(); playBtn = null; }
    if (playHint) { playHint.remove(); playHint = null; }
  } else {
    setAuto("idle");
    root.setAttribute("data-flight-cue", "on");
    measureOverhang();
    /* Re-measure once webfonts have settled: the header above this section can
       change height as they swap in. */
    window.addEventListener("load", measureOverhang);
    window.addEventListener("resize", measureOverhang, { passive: true });
  }

  function startAuto() {
    if (!playBtn || auto) return;
    cancelTween();
    if (!loaded || !duration) {
      /* The fetch is normally well underway by now -- tick() starts it as soon
         as the hero is within two and a half viewports -- so this is a short
         wait, not a cold start. */
      setAuto("loading");
      load();
      var waited = 0;
      var poll = setInterval(function () {
        waited += 100;
        if (loaded && duration) { clearInterval(poll); reallyStart(); }
        else if (waited > 15000 || root.getAttribute("data-flight-auto") !== "loading") {
          clearInterval(poll);
          if (root.getAttribute("data-flight-auto") === "loading") setAuto("idle");
        }
      }, 100);
      return;
    }
    reallyStart();
  }

  function reallyStart() {
    if (!playBtn) return;
    auto = true;
    setAuto("playing");
    dismiss();                    /* the film is the answer now; the ask is done */
    seeking = false;              /* nothing is seeking during playback */
    var pr = video.play();
    if (pr && pr.catch) pr.catch(function () { stopAuto(); });
  }

  /* Give the scrubber back exactly where the film is, or it lerps from a stale
     `current` and the picture jumps the moment the reader takes over. */
  function stopAuto() {
    if (!auto) return;
    auto = false;
    try { video.pause(); } catch (e) {}
    current = target = lastWrite = video.currentTime;
    setAuto("idle");
    dismiss();                    /* the reader took over; do not re-offer */
  }

  /* The end is not a stop: the film has finished, so carry on to the section it
     was an introduction to. */
  function finishAuto() {
    if (!auto) return;
    auto = false;
    try { video.pause(); } catch (e) {}
    current = target = lastWrite = duration;
    setAuto("idle");
    dismiss();
    var dest = document.getElementById("process");
    if (dest) tweenTo(window.scrollY + dest.getBoundingClientRect().top, 800);
  }

  function cancelTween() {
    if (tweening) { cancelAnimationFrame(tweening); tweening = 0; }
  }

  function tweenTo(to, ms) {
    var from = window.scrollY, t0 = performance.now();
    cancelTween();
    (function step(now) {
      var k = Math.min((now - t0) / ms, 1);
      var e = k < 0.5 ? 4 * k * k * k : 1 - Math.pow(-2 * k + 2, 3) / 2;   /* easeInOutCubic */
      window.scrollTo(0, from + (to - from) * e);
      tweening = k < 1 ? requestAnimationFrame(step) : 0;
    })(t0);
  }

  if (playBtn) {
    playBtn.addEventListener("click", function () {
      if (auto) stopAuto(); else startAuto();
    });

    /* Cancel on INPUT, never on scroll. Playback scrolls the page itself, and
       nothing in a scroll event distinguishes our write from the reader's --
       listening for scroll would cancel playback on its own first frame. */
    /* Cancel on INPUT, never on scroll. Playback scrolls the page itself, and
       nothing in a scroll event distinguishes our write from the reader's --
       listening for scroll would cancel playback on its own first frame.

       The same gesture also retires the controls whether or not anything is
       playing: someone who has started scrolling has answered the question the
       play disc was asking. */
    ["wheel", "touchstart", "keydown", "mousedown"].forEach(function (ev) {
      window.addEventListener(ev, function (e) {
        /* The control's own click arrives as mousedown first, so let it through.
           The Node guard is not decoration: `contains` THROWS on anything that
           is not a Node, and an event dispatched at the window has the window
           as its target -- which killed this handler outright, leaving the
           controls on screen forever. */
        if (playBtn && e.target instanceof Node && playBtn.contains(e.target)) return;
        if (auto || tweening) { cancelTween(); stopAuto(); }
        dismiss();
      }, { passive: true });
    });

    document.addEventListener("visibilitychange", function () {
      if (document.hidden) { cancelTween(); stopAuto(); }
    });
  }

  /* ---------------------------------------------- mobile nav */

  var navBtn = root.querySelector("[data-flight-navbtn]");
  var nav = root.querySelector("[data-flight-nav]");
  if (navBtn && nav) {
    navBtn.addEventListener("click", function () {
      var open = root.getAttribute("data-nav") === "open";
      root.setAttribute("data-nav", open ? "closed" : "open");
      navBtn.setAttribute("aria-expanded", open ? "false" : "true");
    });
    nav.addEventListener("keydown", function (e) {
      if (e.key === "Escape") {
        root.setAttribute("data-nav", "closed");
        navBtn.setAttribute("aria-expanded", "false");
        navBtn.focus();
      }
    });
  }
})();
